import {
  App,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { getTaskAssignees } from 'api/tasks';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';

function AddTaskDrawer({
  open, onClose, onSubmit, task,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const { message } = App.useApp();
  const isEdit = Boolean(task);

  useEffect(() => {
    if (!open) return;

    setAssigneesLoading(true);
    getTaskAssignees()
      .then((res) => {
        const data = getData(res);
        const users = data?.data ?? data ?? [];
        setAssignees(Array.isArray(users) ? users : []);
      })
      .catch((error) => {
        captureException(error);
        message.error(getHttpErrorMessage(error) || 'Failed to load task owners');
      })
      .finally(() => setAssigneesLoading(false));
  }, [message, open]);

  useEffect(() => {
    if (!open) return;
    if (task) {
      form.setFieldsValue({
        title: task.title,
        description: task.description || '',
        due_at: task.due_at ? dayjs(task.due_at) : undefined,
        assigned_to_id: task.assigned_to?.id,
      });
      return;
    }
    form.resetFields();
  }, [form, open, task]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      setLoading(true);
      const payload = {
        title: values.title.trim(),
        description: values.description?.trim() || '',
        due_at: values.due_at ? values.due_at.toISOString() : null,
      };
      if (isEdit) {
        payload.assigned_to_id = values.assigned_to_id ?? null;
      } else if (values.assigned_to_id) {
        payload.assigned_to_id = values.assigned_to_id;
      }
      onSubmit(payload)
        .then(() => form.resetFields())
        .catch((error) => {
          captureException(error);
          message.error(getHttpErrorMessage(error) || (isEdit ? 'Failed to update task' : 'Failed to create task'));
        })
        .finally(() => setLoading(false));
    }).catch(() => {});
  };

  const ownerOptions = useMemo(() => {
    const users = [...assignees];
    if (task?.assigned_to?.id && !users.some((user) => user.id === task.assigned_to.id)) {
      users.push(task.assigned_to);
    }
    return users.map((user) => {
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
      return {
        value: user.id,
        label: fullName ? `${fullName} (${user.email})` : user.email,
      };
    });
  }, [assignees, task]);

  return (
    <Drawer
      title={isEdit ? 'Edit Task' : 'Create Task'}
      width={400}
      open={open}
      onClose={handleClose}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {isEdit ? 'Save' : 'Create Task'}
          </Button>
        </div>
      )}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, whitespace: true, message: 'Enter a task title' }]}
        >
          <Input maxLength={200} placeholder="e.g. Follow up about catering order" />
        </Form.Item>
        <Form.Item name="description" label="Notes / Description">
          <Input.TextArea rows={4} placeholder="Add context for this follow-up..." />
        </Form.Item>
        <Form.Item name="due_at" label="Due Date">
          <DatePicker
            showTime={{ format: 'HH:mm', showSecond: false }}
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="assigned_to_id"
          label="Assigned Owner"
          extra={isEdit ? undefined : 'Leave blank to assign the task to yourself.'}
        >
          <Select
            allowClear
            showSearch
            loading={assigneesLoading}
            options={ownerOptions}
            optionFilterProp="label"
            placeholder="Select an owner"
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export default AddTaskDrawer;
